import { Fragment, useState, useCallback, useRef } from 'react';
import {
    ChevronDown, ChevronUp, ChevronsUpDown, ExternalLink, FileText, Info,
} from 'lucide-react';
import { formatDate, formatDateTimeCH } from '../../utils/format';
import { getBcPostedPurchaseUrl, bcLinkHint } from '../../utils/bcLinks';
import {
    isNewArrival, matchCaveat, docuWareSectionCount,
    type DocuWareInvoiceRow, type DocuWareSortField,
} from '../../types/docuwareInvoices';
import { groupIndexFields, countEmptyIndexFields } from '../../types/docuwareFieldLabels';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { ColumnResizeHandle } from '../ui/ColumnResizeHandle';
import { DocuWareDocLink } from './DocuWareDocLink';
import { DocuWarePdfViewerModal } from './DocuWarePdfViewerModal';

/**
 * The invoice table.
 *
 * Sorting is SERVER-side, so the headers only report a click — they never reorder `rows`.
 * The server-sorted pages in this repo render their headers from a COLS array rather than
 * using components/payment-recon/SortableTh (which pairs with the client-side useTableSort);
 * this follows PowerBiUsageTable.
 *
 * All timestamps go through formatDateTimeCH — they are UTC in the database and Swiss on
 * screen. `invoiceDate` and `bcPostingDate` are calendar days and use formatDate, which never
 * constructs a Date at all.
 */

interface Col {
    key: string;
    label: string;
    sort?: DocuWareSortField;
    align?: 'right';
    className?: string;
    /** Starting width in px. The user's drag overrides it and outlives the tab (localStorage). */
    width: number;
}

const WIDTH_KEY = 'docuware-invoices-col-widths';

const COLS: Col[] = [
    { key: 'expand', label: '', className: 'w-8', width: 32 },
    { key: 'company', label: 'Company', sort: 'company', width: 110 },
    // Two arrival dates, and they answer different questions. "Stored" is when DocuWare
    // archived the document — the real arrival. "Received" is when THIS app first saw it,
    // which for everything loaded by the Phase-1 backfill is one afternoon in August 2026.
    { key: 'stored', label: 'Stored in DocuWare', sort: 'stored', width: 150 },
    { key: 'received', label: 'Received', sort: 'received', width: 150 },
    // Supplier is the one that needs the room — "VIBRANTZ COLOR SOLUTIONS EUROPE B.V., 6136
    // KV Sittard - 703123" is a normal value, not an outlier.
    { key: 'supplier', label: 'Supplier', sort: 'supplier', width: 320 },
    { key: 'invoiceNumber', label: 'Invoice No.', sort: 'invoiceNumber', width: 140 },
    { key: 'invoiceDate', label: 'Invoice Date', sort: 'invoiceDate', width: 110 },
    { key: 'amount', label: 'Amount', sort: 'amount', align: 'right', width: 110 },
    { key: 'currency', label: 'Currency', sort: 'currency', width: 80 },
    { key: 'bc', label: 'BC Document', sort: 'bcDocumentNo', width: 200 },
    // Room for the PDF button next to the DocuWare link (the link's no-URL state is wider still).
    { key: 'document', label: 'Document', width: 220 },
];

function formatAmount(value: number | null): string {
    if (value === null || value === undefined) return '–';
    return value.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function renderIndexValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '–';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

interface Props {
    rows: DocuWareInvoiceRow[];
    sortBy: DocuWareSortField;
    sortOrder: 'asc' | 'desc';
    onSort: (field: DocuWareSortField) => void;
    onCopyDocId: (docId: string) => void;
}

export function DocuWareInvoiceTable({ rows, sortBy, sortOrder, onSort, onCopyDocId }: Props) {
    const [expandedId, setExpandedId] = useState<string | null>(null);
    // Reset with every expansion: "show all" is a per-inspection choice, not a page
    // preference. Carrying it to the next row would quietly reintroduce the 80-line wall it
    // exists to avoid.
    const [showAllFields, setShowAllFields] = useState(false);

    const toggleRow = useCallback((id: string) => {
        setExpandedId((current) => (current === id ? null : id));
        setShowAllFields(false);
    }, []);

    // The row whose PDF window is open, and the button that opened it. useDialog returns focus
    // to whatever was focused at open time, but Safari does not focus a button on click, so the
    // opener is remembered explicitly: closing puts the keyboard user back on THEIR row.
    const [pdfRow, setPdfRow] = useState<DocuWareInvoiceRow | null>(null);
    const pdfOpener = useRef<HTMLElement | null>(null);
    const openPdf = useCallback((row: DocuWareInvoiceRow, opener: HTMLElement) => {
        pdfOpener.current = opener;
        setPdfRow(row);
    }, []);
    const closePdf = useCallback(() => {
        setPdfRow(null);
        const opener = pdfOpener.current;
        pdfOpener.current = null;
        // After the portal has unmounted; skipped when a reload has replaced the row's button.
        window.setTimeout(() => { if (opener && document.contains(opener)) opener.focus(); }, 0);
    }, []);

    const { widthOf, totalWidthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLS, WIDTH_KEY);
    const totalWidth = totalWidthOf(COLS.map(c => c.key));

    return (
        // The scroll container is what the sticky header sticks INSIDE of — a sticky thead in
        // a container that does not scroll has nothing to stick to. max-h keeps the filters
        // and the row count on screen while the table scrolls under its own header.
        <div className="max-h-[70vh] overflow-auto">
            {/* table-fixed so a long supplier name is clipped instead of shoving every other
                column out of the width the user set; minWidth (not width) lets the table fill
                a wide container and scroll horizontally in a narrow one. */}
            <table className="w-full table-fixed text-sm" style={{ minWidth: totalWidth }}>
                <colgroup>
                    {COLS.map(col => <col key={col.key} style={{ width: widthOf(col.key) }} />)}
                </colgroup>
                {/* bg-gray-50 must be explicit: a transparent thead lets the rows scroll
                    through it. The bottom rule is an inset shadow because a border on a
                    sticky th does not travel with it. */}
                <thead className="sticky top-0 z-20 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 shadow-[inset_0_-1px_0_rgb(229,231,235)]">
                    <tr>
                        {COLS.map((col) => (
                            <th
                                key={col.key}
                                className={`relative px-3 py-2 font-medium ${col.className ?? ''} ${
                                    col.align === 'right' ? 'text-right' : ''
                                }`}
                            >
                                {col.sort ? (
                                    <button
                                        type="button"
                                        // The click that ENDS a drag lands here; without this
                                        // guard every resize also re-sorts the table.
                                        onClick={() => { if (!shouldSuppressClick()) onSort(col.sort!); }}
                                        className={`group inline-flex max-w-full items-center gap-1 truncate uppercase tracking-wide hover:text-gray-900 ${
                                            col.align === 'right' ? 'flex-row-reverse' : ''
                                        } ${sortBy === col.sort ? 'text-gray-900' : ''}`}
                                        title={`Sort by ${col.label}`}
                                    >
                                        <span className="truncate">{col.label}</span>
                                        {sortBy === col.sort ? (
                                            sortOrder === 'asc'
                                                ? <ChevronUp className="h-3.5 w-3.5 shrink-0" />
                                                : <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                                        ) : (
                                            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-gray-300 group-hover:text-gray-400" />
                                        )}
                                    </button>
                                ) : col.label}
                                <ColumnResizeHandle
                                    label={col.label || col.key}
                                    active={resizingKey === col.key}
                                    onStart={(x) => startResize(col.key, x)}
                                    onReset={() => resetWidth(col.key)}
                                />
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {rows.map((row) => {
                        const expanded = expandedId === row.id;
                        const fieldCount = row.indexFields ? Object.keys(row.indexFields).length : 0;
                        const emptyCount = row.indexFields ? countEmptyIndexFields(row.indexFields) : 0;
                        const groups = row.indexFields
                            ? groupIndexFields(row.indexFields, showAllFields, row.resolvedUsers)
                            : [];
                        // null for dbi (redPoint has no reachable web client) and for any
                        // posting the matcher has not classified. isEosCompany is the whole
                        // gate — no company is named here.
                        const bcUrl = getBcPostedPurchaseUrl(row.companyCode, row.bcDocumentNo, row.bcDocumentKind);
                        const caveat = matchCaveat(row);
                        const sectionCount = docuWareSectionCount(row);

                        return (
                            <Fragment key={row.id}>
                                <tr className="cursor-pointer hover:bg-gray-50" onClick={() => toggleRow(row.id)}>
                                    <td className="px-2 py-2 text-gray-400">
                                        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2">
                                        <span className="font-medium text-gray-700">{row.companyCode}</span>
                                        {row.source === 'seed' && (
                                            <span className="ml-2 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-purple-700">
                                                Seed
                                            </span>
                                        )}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2">{formatDateTimeCH(row.storedAt)}</td>
                                    <td className="whitespace-nowrap px-3 py-2">
                                        {formatDateTimeCH(row.firstSeenAt)}
                                        {isNewArrival(row.firstSeenAt) && (
                                            <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-green-700">
                                                New
                                            </span>
                                        )}
                                    </td>
                                    {/* table-fixed clips rather than pushes, so the full value
                                        has to stay reachable — hence title. */}
                                    <td className="truncate px-3 py-2" title={row.supplier ?? undefined}>
                                        {row.supplier ?? '–'}
                                    </td>
                                    <td className="truncate px-3 py-2 font-mono text-xs" title={row.invoiceNumber ?? undefined}>
                                        {row.invoiceNumber ?? '–'}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2">{formatDate(row.invoiceDate)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{formatAmount(row.amount)}</td>
                                    <td className="px-3 py-2">{row.currency ?? '–'}</td>

                                    {/* BC posting. Not posted yet is a real, expected answer — it is
                                        what the page was built to show — so it reads as a quiet dash,
                                        not as an error. */}
                                    <td className="whitespace-nowrap px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                        {!row.bcDocumentNo ? (
                                            <span className="text-xs text-gray-400">not posted</span>
                                        ) : (
                                            <div className="flex items-center gap-1.5">
                                                {bcUrl ? (
                                                    <a
                                                        href={bcUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1 font-mono text-xs text-blue-600 hover:text-blue-800"
                                                        title={bcLinkHint(row.bcDocumentKind)}
                                                    >
                                                        {row.bcDocumentNo}
                                                        <ExternalLink className="h-3 w-3" />
                                                    </a>
                                                ) : (
                                                    <span className="font-mono text-xs text-gray-700">{row.bcDocumentNo}</span>
                                                )}
                                                {caveat && (
                                                    <span title={caveat}>
                                                        <Info className="h-3.5 w-3.5 text-amber-500" />
                                                    </span>
                                                )}
                                                {/* A collective invoice says so on the row, not
                                                    only in a tooltip: the number beside it is
                                                    the FIRST of several postings, and a reader
                                                    who takes it for the whole invoice has been
                                                    misled by the display rather than the data. */}
                                                {row.postingCount > 1 && (
                                                    <span
                                                        className="rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-700"
                                                        title={`This invoice was posted as ${row.postingCount} documents: ${row.bcPostings ?? row.bcDocumentNo}`}
                                                    >
                                                        +{row.postingCount - 1}
                                                    </span>
                                                )}
                                                {row.bcPostingDate && (
                                                    <span className="text-[11px] text-gray-500">
                                                        {formatDate(row.bcPostingDate)}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </td>

                                    <td className="whitespace-nowrap px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center gap-3">
                                            {/* 0 sections (49 dit documents) can only answer 404, so no
                                                button; the DocuWare link beside it stays. */}
                                            {sectionCount > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => openPdf(row, e.currentTarget)}
                                                    aria-haspopup="dialog"
                                                    aria-label={`View PDF of ${row.invoiceNumber ? `invoice ${row.invoiceNumber}` : 'this document'}${row.supplier ? `, ${row.supplier}` : ''}`}
                                                    className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 px-2 text-xs font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                                >
                                                    <FileText aria-hidden="true" className="h-4 w-4" />
                                                    View PDF
                                                </button>
                                            )}
                                            <DocuWareDocLink row={row} onCopyDocId={onCopyDocId} />
                                        </div>
                                    </td>
                                </tr>

                                {expanded && (
                                    <tr className="bg-gray-50">
                                        <td colSpan={COLS.length} className="px-6 py-4">
                                            {row.bcDocumentNo && (
                                                <div className="mb-3 rounded border border-gray-200 bg-white p-2">
                                                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                                        Business Central posting
                                                    </div>
                                                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-gray-600">
                                                        <span>Document: <span className="font-mono text-gray-800">{row.bcDocumentNo}</span></span>
                                                        {/* Every posting, spelled out. Migration 192
                                                            made one invoice able to be several
                                                            postings; until this line existed the
                                                            panel still showed only the first. */}
                                                        {row.postingCount > 1 && (
                                                            <span className="text-amber-700">
                                                                All postings:{' '}
                                                                <span className="font-mono">{row.bcPostings}</span>
                                                            </span>
                                                        )}
                                                        <span>Posted: {formatDate(row.bcPostingDate)}</span>
                                                        <span>Type: {row.bcDocumentType ?? '–'}</span>
                                                        {/* Where it lives, which is a different question from what it
                                                            is — and the one that decides whether a link can exist. */}
                                                        {row.bcDocumentKind === 'ledger' && (
                                                            <span className="text-amber-700">Journal posting (no document card)</span>
                                                        )}
                                                        <span>Vendor: <span className="font-mono">{row.bcVendorNo ?? '–'}</span></span>
                                                        <span>
                                                            Posted amount:{' '}
                                                            <span className="tabular-nums">{formatAmount(row.bcAmount)}</span>
                                                            {row.bcCurrency ? ` ${row.bcCurrency}` : ''}
                                                        </span>
                                                        {/* How the link was established, in the open. A number
                                                            without its provenance invites more trust than it earns. */}
                                                        <span>Matched by: {row.matchMethod ?? '–'} ({row.matchConfidence ?? 0}%)</span>
                                                    </div>
                                                    {caveat && (
                                                        <p className="mt-1.5 flex items-start gap-1 text-[11px] text-amber-700">
                                                            <Info className="mt-px h-3.5 w-3.5 shrink-0" />
                                                            {caveat}
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            <div className="mb-2 flex items-center justify-between gap-4">
                                                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                    Index fields
                                                </span>
                                                {row.indexFieldsRaw === null && emptyCount > 0 && (
                                                    <button
                                                        onClick={() => setShowAllFields((v) => !v)}
                                                        className="text-xs text-blue-600 hover:text-blue-800"
                                                    >
                                                        {showAllFields
                                                            ? `Hide ${emptyCount} empty fields`
                                                            : `Show all ${fieldCount} fields (${emptyCount} empty)`}
                                                    </button>
                                                )}
                                            </div>
                                            {row.indexFieldsRaw !== null ? (
                                                <>
                                                    <p className="mb-2 text-xs text-amber-700">
                                                        Stored payload is not valid JSON — shown verbatim.
                                                    </p>
                                                    <pre className="whitespace-pre-wrap break-all font-mono text-xs text-gray-700">
                                                        {row.indexFieldsRaw}
                                                    </pre>
                                                </>
                                            ) : fieldCount === 0 ? (
                                                <p className="text-xs text-gray-500">No index fields stored.</p>
                                            ) : (
                                                /* Grouped and named. The raw DocuWare field name stays visible under
                                                   every label — the expansion is still the field-discovery UI, and a
                                                   friendly name must never make the technical one unfindable. */
                                                <div className="space-y-3">
                                                    {groups.map((g) => (
                                                        <div key={g.group}>
                                                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                                                {g.groupLabel}
                                                            </div>
                                                            <dl className="grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
                                                                {g.entries.map((e) => (
                                                                    <div key={e.field} className="flex gap-2 text-xs">
                                                                        <dt className="min-w-[11rem] text-gray-500">
                                                                            {e.label}
                                                                            <span className="ml-1 font-mono text-[10px] text-gray-400">
                                                                                {e.field}
                                                                            </span>
                                                                        </dt>
                                                                        {/* A resolved login shows the person FIRST and keeps the
                                                                            raw login beside it. Adding the name must not cost the
                                                                            reader the value DocuWare actually stored. */}
                                                                        <dd className={`break-all ${e.empty ? 'text-gray-400' : 'text-gray-800'}`}>
                                                                            {e.resolved ? (
                                                                                <>
                                                                                    <span className={e.resolved.isActive ? '' : 'text-gray-500'}>
                                                                                        {e.resolved.display}
                                                                                    </span>
                                                                                    {!e.resolved.isActive && (
                                                                                        <span className="ml-1 text-[10px] text-gray-400">(left)</span>
                                                                                    )}
                                                                                    <span className="ml-1 font-mono text-[10px] text-gray-400">
                                                                                        {renderIndexValue(e.value)}
                                                                                    </span>
                                                                                </>
                                                                            ) : (
                                                                                <span className="font-mono">{renderIndexValue(e.value)}</span>
                                                                            )}
                                                                        </dd>
                                                                    </div>
                                                                ))}
                                                            </dl>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-gray-500">
                                                <span>Document ID: <span className="font-mono">{row.docId ?? '–'}</span></span>
                                                <span>File cabinet: <span className="font-mono">{row.fileCabinetId ?? '–'}</span></span>
                                                <span>Stored in DocuWare: {formatDateTimeCH(row.storedAt)}</span>
                                                <span>Last modified: {formatDateTimeCH(row.modifiedAt)}</span>
                                                <span>Source: {row.source}</span>
                                                <span>Last seen: {formatDateTimeCH(row.lastSeenAt)}</span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>
            {pdfRow && (
                <DocuWarePdfViewerModal
                    row={pdfRow}
                    sectionCount={docuWareSectionCount(pdfRow)}
                    onCopyDocId={onCopyDocId}
                    onClose={closePdf}
                />
            )}
        </div>
    );
}
